// pages/secure/OfficeDirectoryPage.js
// Walk the halls: every agent's and employee's door, who's in, and a look
// inside any office (license wall, bio, contact) without leaving the hallway.

import React, { useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const DoorCard = ({ person, onVisit }) => {
    const isIn = person.door_open !== 0 && person.door_open !== null;
    return (
        <button onClick={() => onVisit(person)}
                className="text-left bg-white rounded-lg shadow hover:shadow-md transition-shadow p-5 border-t-4 border-slate-700 w-full">
            <div className="flex items-center gap-3">
                {person.photo_path
                    ? <img src={`/office-media/${person.photo_path}`} alt="" className="w-12 h-12 rounded-full object-cover" />
                    : <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-500">
                        {(person.name || person.username || '?').charAt(0)}
                      </div>}
                <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{person.name || person.username}</p>
                    <p className="text-sm text-slate-500 truncate">
                        {person.display_title || person.staff_role || (person.agent_id ? 'Licensed Broker' : 'Staff')}
                    </p>
                </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
                <span className={`flex items-center gap-1.5 text-xs font-medium ${isIn ? 'text-green-600' : 'text-slate-400'}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${isIn ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                    {isIn ? 'Door open' : 'Away'}
                </span>
                <span className="flex gap-1">
                    {(person.is_managing_broker === 1 || person.is_designated_manager === 1) &&
                        <span className="text-xs bg-slate-800 text-white px-2 py-0.5 rounded">Managing Broker</span>}
                </span>
            </div>
            {person.status_message && <p className="mt-2 text-xs text-slate-500 italic truncate">“{person.status_message}”</p>}
        </button>
    );
};

const OfficeModal = ({ person, onClose }) => {
    if (!person) return null;
    const licenseExpired = person.license_expiration_date && new Date(person.license_expiration_date) < new Date();
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        {person.photo_path
                            ? <img src={`/office-media/${person.photo_path}`} alt="" className="w-16 h-16 rounded-full object-cover" />
                            : <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-500">
                                {(person.name || person.username || '?').charAt(0)}
                              </div>}
                        <div>
                            <p className="text-lg font-semibold text-slate-800">{person.name || person.username}</p>
                            <p className="text-slate-500">{person.display_title || person.staff_role || (person.agent_id ? 'Licensed Broker' : 'Staff')}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
                </div>

                {person.agent_id && person.show_license_wall !== 0 && (
                    <div className={`border rounded-md p-4 mt-4 ${licenseExpired ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Illinois Real Estate License</p>
                        <p className="font-mono text-slate-800">{person.license_number || '—'}</p>
                        {person.license_expiration_date &&
                            <p className={`text-sm ${licenseExpired ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                                {licenseExpired ? 'EXPIRED' : 'Expires'} {new Date(person.license_expiration_date).toLocaleDateString()}
                            </p>}
                    </div>
                )}

                {person.bio && <p className="mt-4 text-slate-700 whitespace-pre-wrap">{person.bio}</p>}

                <div className="mt-4 text-sm text-slate-600 space-y-1">
                    {person.public_email && <p>✉️ <a className="text-blue-600 hover:underline" href={`mailto:${person.public_email}`}>{person.public_email}</a></p>}
                    {person.public_phone && <p>📞 {person.public_phone}</p>}
                    {person.status_message && <p className="italic text-slate-500">“{person.status_message}”</p>}
                </div>
            </div>
        </div>
    );
};

const OfficeDirectoryPage = () => {
    const apiClient = useApiClient();
    const [people, setPeople] = useState([]);
    const [visiting, setVisiting] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/api/office/directory')
            .then(setPeople)
            .catch(err => setError(err.message || 'Failed to load the directory.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    const agents = people.filter(p => p.agent_id);
    const staff = people.filter(p => !p.agent_id && p.employee_id);

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Office Directory</h1>
            <p className="text-slate-500 mb-6">Walk the halls — see who's in and stop by any door.</p>

            {isLoading && <p>Walking the halls...</p>}
            {error && <p className="text-red-500">{error}</p>}

            {staff.length > 0 && (
                <>
                    <h2 className="text-lg font-semibold text-slate-700 mb-3">Office Staff</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-8">
                        {staff.map(p => <DoorCard key={p.user_id} person={p} onVisit={setVisiting} />)}
                    </div>
                </>
            )}

            {agents.length > 0 && (
                <>
                    <h2 className="text-lg font-semibold text-slate-700 mb-3">Agents</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {agents.map(p => <DoorCard key={p.user_id} person={p} onVisit={setVisiting} />)}
                    </div>
                </>
            )}

            <OfficeModal person={visiting} onClose={() => setVisiting(null)} />
        </div>
    );
};

export default OfficeDirectoryPage;
